import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MobileStickyBar from '@/components/feature/MobileStickyBar';
import useSEO from '@/hooks/useSEO';

const reviews = [
  { name: 'Michael T.', location: 'Summerlin, NV', rating: 5, project: 'Frameless Shower Door', date: 'March 2024', text: 'Quinolux installed a frameless shower door in our master bath and it looks absolutely stunning. The craftsmanship is flawless — no gaps, perfect seal, and the hardware is top quality. The team was professional, clean, and finished in under 2 hours. Highly recommend!', avatar: 'https://public.readdy.ai/ai/img_res/f1a61575a833b5249952661b69c71d10.jpg' },
  { name: 'Sarah K.', location: 'Henderson, NV', rating: 5, project: 'Glass Railings', date: 'February 2024', text: 'We had glass railings installed on our staircase and balcony. The team was professional, on time, and the finished product exceeded our expectations. Our home looks like a luxury showroom now! The glass is crystal clear and the hardware is beautiful.', avatar: 'https://readdy.ai/api/search-image?query=professional%20headshot%20of%20young%20woman%20smiling%2C%20clean%20white%20background%2C%20friendly%20expression%2C%20business%20casual%20attire&width=100&height=100&seq=rev002&orientation=squarish' },
  { name: 'David R.', location: 'Las Vegas, NV', rating: 5, project: 'Custom Gym Mirrors', date: 'January 2024', text: 'Got custom mirrors for our home gym and they look incredible. The team measured everything perfectly and the installation was clean and fast. Will definitely use Quinolux again for our next project. Best glass company in Las Vegas!', avatar: 'https://readdy.ai/api/search-image?query=professional%20headshot%20of%20man%20in%20his%2040s%20smiling%2C%20clean%20white%20background%2C%20confident%20expression%2C%20casual%20shirt&width=100&height=100&seq=rev003&orientation=squarish' },
  { name: 'Jennifer M.', location: 'Spring Valley, NV', rating: 5, project: 'Shower Enclosure', date: 'December 2023', text: 'From the initial quote to the final installation, the entire experience was seamless. They were transparent about pricing, showed up on time, and the frameless shower enclosure is absolutely beautiful. 10/10 would recommend.', avatar: 'https://readdy.ai/api/search-image?query=professional%20headshot%20of%20woman%20in%20her%2030s%20smiling%20warmly%2C%20clean%20white%20background%2C%20friendly%20professional%20look&width=100&height=100&seq=rev004&orientation=squarish' },
  { name: 'Robert L.', location: 'North Las Vegas, NV', rating: 5, project: 'Tub Enclosure', date: 'November 2023', text: 'Had a glass tub enclosure installed and it completely transformed our bathroom. The sliding door glides perfectly and the seal is watertight. Great price, great service, great result. Quinolux is the real deal.', avatar: 'https://readdy.ai/api/search-image?query=professional%20headshot%20of%20older%20man%20smiling%2C%20clean%20white%20background%2C%20friendly%20expression%2C%20retired%20professional&width=100&height=100&seq=rev005&orientation=squarish' },
  { name: 'Amanda C.', location: 'Paradise, NV', rating: 5, project: 'Custom Mirrors', date: 'October 2023', text: 'I needed custom mirrors for my salon and Quinolux delivered beyond my expectations. The mirrors are perfectly cut, beautifully installed, and my clients love them. Professional, fast, and fairly priced.', avatar: 'https://readdy.ai/api/search-image?query=professional%20headshot%20of%20woman%20in%20her%2040s%20smiling%2C%20clean%20white%20background%2C%20business%20owner%20look%2C%20friendly&width=100&height=100&seq=rev006&orientation=squarish' },
  { name: 'Carlos V.', location: 'Summerlin, NV', rating: 5, project: 'Glass Partitions', date: 'September 2023', text: 'We needed glass partitions for our office and Quinolux did an amazing job. The frosted glass looks professional and the installation was done over a weekend so we didn\'t lose any business days. Excellent work!', avatar: 'https://readdy.ai/api/search-image?query=professional%20headshot%20of%20Hispanic%20man%20in%20his%2030s%20smiling%2C%20clean%20white%20background%2C%20business%20professional&width=100&height=100&seq=rev007&orientation=squarish' },
  { name: 'Lisa H.', location: 'Henderson, NV', rating: 5, project: 'Frameless Shower Door', date: 'August 2023', text: 'Second time using Quinolux — first for our master bath, now for the guest bath. Same excellent quality and service both times. They\'re our go-to glass company in Las Vegas. The frameless doors look incredible.', avatar: 'https://readdy.ai/api/search-image?query=professional%20headshot%20of%20woman%20in%20her%2050s%20smiling%2C%20clean%20white%20background%2C%20warm%20friendly%20expression&width=100&height=100&seq=rev008&orientation=squarish' },
];

const ReviewsPage = () => {
  useSEO({
    title: 'Customer Reviews | Quinolux Glass & Mirrors Las Vegas',
    description: 'Read 500+ five-star reviews for Quinolux Glass & Mirrors — Las Vegas\'s top-rated glass company. Real customers, real results. Frameless shower doors, glass railings, custom mirrors.',
    keywords: 'Quinolux reviews Las Vegas, glass company reviews Las Vegas, shower door installation reviews, best glass company Las Vegas',
    canonical: 'https://quinoluxglass.com/reviews',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Customer Reviews | Quinolux Glass & Mirrors Las Vegas',
        description: '500+ five-star reviews for Quinolux Glass & Mirrors — Las Vegas top-rated glass company.',
        url: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/reviews`,
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/` },
            { '@type': 'ListItem', position: 2, name: 'Reviews', item: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/reviews` },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: 'Quinolux Glass & Mirrors',
        url: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}`,
        telephone: '+17026042093',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Las Vegas',
          addressRegion: 'NV',
          addressCountry: 'US',
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '5.0',
          reviewCount: '500',
          bestRating: '5',
          worstRating: '1',
        },
        review: [
          {
            '@type': 'Review',
            author: { '@type': 'Person', name: 'Michael T.' },
            reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
            datePublished: '2024-03-01',
            reviewBody: 'Quinolux installed a frameless shower door in our master bath and it looks absolutely stunning. The craftsmanship is flawless — no gaps, perfect seal, and the hardware is top quality.',
            name: 'Frameless Shower Door Installation',
          },
          {
            '@type': 'Review',
            author: { '@type': 'Person', name: 'Sarah K.' },
            reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
            datePublished: '2024-02-01',
            reviewBody: 'We had glass railings installed on our staircase and balcony. The team was professional, on time, and the finished product exceeded our expectations.',
            name: 'Glass Railing Installation',
          },
          {
            '@type': 'Review',
            author: { '@type': 'Person', name: 'David R.' },
            reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
            datePublished: '2024-01-01',
            reviewBody: 'Got custom mirrors for our home gym and they look incredible. Best glass company in Las Vegas!',
            name: 'Custom Gym Mirrors',
          },
          {
            '@type': 'Review',
            author: { '@type': 'Person', name: 'Jennifer M.' },
            reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
            datePublished: '2023-12-01',
            reviewBody: 'From the initial quote to the final installation, the entire experience was seamless. The frameless shower enclosure is absolutely beautiful. 10/10 would recommend.',
            name: 'Shower Enclosure Installation',
          },
          {
            '@type': 'Review',
            author: { '@type': 'Person', name: 'Robert L.' },
            reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
            datePublished: '2023-11-01',
            reviewBody: 'Had a glass tub enclosure installed and it completely transformed our bathroom. The sliding door glides perfectly and the seal is watertight.',
            name: 'Glass Tub Enclosure',
          },
          {
            '@type': 'Review',
            author: { '@type': 'Person', name: 'Amanda C.' },
            reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
            datePublished: '2023-10-01',
            reviewBody: 'I needed custom mirrors for my salon and Quinolux delivered beyond my expectations. Professional, fast, and fairly priced.',
            name: 'Custom Salon Mirrors',
          },
          {
            '@type': 'Review',
            author: { '@type': 'Person', name: 'Carlos V.' },
            reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
            datePublished: '2023-09-01',
            reviewBody: 'We needed glass partitions for our office and Quinolux did an amazing job. The frosted glass looks professional and the installation was done over a weekend.',
            name: 'Office Glass Partitions',
          },
          {
            '@type': 'Review',
            author: { '@type': 'Person', name: 'Lisa H.' },
            reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
            datePublished: '2023-08-01',
            reviewBody: 'Second time using Quinolux — same excellent quality and service both times. They\'re our go-to glass company in Las Vegas.',
            name: 'Repeat Customer — Frameless Shower Doors',
          },
        ],
      },
    ],
  });

  return (
    <main>
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#F8F8F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 text-center">
          <div className="inline-flex items-center gap-2 bg-black text-white text-xs font-semibold px-4 py-2 rounded-full mb-6">
            <i className="ri-star-fill text-yellow-400"></i>
            Rated 5.0 on Google
          </div>
          <h1 className="text-5xl md:text-6xl font-light text-black mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Customer Reviews
          </h1>
          <p className="text-gray-500 text-base max-w-xl mx-auto">
            Don't take our word for it — here's what Las Vegas homeowners and businesses say about Quinolux Glass & Mirrors.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 mt-10">
            {[{ num: '500+', label: 'Google Reviews' }, { num: '5.0', label: 'Average Rating' }, { num: '98%', label: 'Would Recommend' }].map(s => (
              <div key={s.label} className="text-center">
                <p className="text-4xl font-bold text-black">{s.num}</p>
                <p className="text-gray-500 text-sm mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map(review => (
              <div key={review.name} className="bg-[#F8F8F6] rounded-2xl p-6 flex flex-col">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: review.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-sm"></i>
                  ))}
                </div>
                <p className="text-gray-700 text-sm leading-relaxed flex-1 mb-5">&ldquo;{review.text}&rdquo;</p>
                <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                  <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                    <img src={review.avatar} alt={review.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-black font-semibold text-sm">{review.name}</p>
                    <p className="text-gray-400 text-xs truncate">{review.location} · {review.project}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <i className="ri-google-fill text-gray-300 text-lg"></i>
                    <span className="text-gray-400 text-xs">{review.date}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-black text-white text-center">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-4xl font-light mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Join Our Happy Customers</h2>
          <p className="text-gray-400 text-sm mb-8">Get a free estimate and experience the Quinolux difference for yourself.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/free-quote" className="inline-flex items-center gap-2 bg-white text-black text-sm font-semibold px-8 py-4 rounded-full hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">Get Free Quote</Link>
            <a href="tel:+17026042093" className="inline-flex items-center gap-2 border border-gray-600 text-white text-sm font-medium px-8 py-4 rounded-full hover:border-white transition-colors whitespace-nowrap cursor-pointer"><i className="ri-phone-fill"></i>(702) 604-2093</a>
          </div>
        </div>
      </section>

      <Footer />
      <MobileStickyBar />
    </main>
  );
};

export default ReviewsPage;
